import React, { useState, useEffect } from 'react';
import { Box, Alert, Skeleton } from '@mui/material';
import { WalteroCard, WalteroTypography, WalteroButton, WalteroDataTable } from '../waltero';
import { subscriptionService } from '../../services/subscriptionService';
import { fetchOrganizationById } from '../../services/apiService';
import AssignSubscriptionModal from './AssignSubscriptionModal';
import EditOrganizationSubscriptionModal from './EditOrganizationSubscriptionModal';

const OrganizationSubscriptions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organizationSubscriptions, setOrganizationSubscriptions] = useState([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [organizationCache, setOrganizationCache] = useState(new Map());
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [defaultSubscriptionsMap, setDefaultSubscriptionsMap] = useState(new Map());
  const [organizationSubscriptionCounts, setOrganizationSubscriptionCounts] = useState(new Map());
  const [unassigningId, setUnassigningId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load subscription data and subscription types
      const [orgSubscriptionsResult, subscriptionTypesResult] = await Promise.all([
        subscriptionService.getAllOrganizationSubscriptions(),
        subscriptionService.getSubscriptionTypes()
      ]);

      console.log('🔍 Organization subscriptions loaded:', orgSubscriptionsResult.data?.length || 0);
      console.log('🔍 Subscription types loaded:', subscriptionTypesResult.data?.length || 0);

      const subscriptions = orgSubscriptionsResult.data || [];
      setOrganizationSubscriptions(subscriptions);
      setSubscriptionTypes(subscriptionTypesResult.data || []);

      const defaultsMap = new Map();
      const countsMap = new Map();

      subscriptions.forEach(sub => {
        if (!sub.organizationId) {
          return;
        }
        if (sub.status !== 'inactive') {
          countsMap.set(sub.organizationId, (countsMap.get(sub.organizationId) || 0) + 1);
        }
        if (sub.isDefault) {
          defaultsMap.set(sub.organizationId, sub.subscriptionTypeId);
        }
      });

      setDefaultSubscriptionsMap(defaultsMap);
      setOrganizationSubscriptionCounts(countsMap);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async (event, subscription) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!subscription?.id) {
      return;
    }

    const confirmed = window.confirm(
      'Remove this subscription from the organization? Sensors currently assigned to it will fall back to the default.'
    );

    if (!confirmed) {
      return;
    }

    try {
      setUnassigningId(subscription.id);
      setError(null);
      await subscriptionService.deleteOrganizationSubscription(subscription.id);
      await loadData();
    } catch (err) {
      console.error('Failed to delete organization subscription:', err);
      setError(err.message || 'Failed to delete organization subscription');
    } finally {
      setUnassigningId(null);
    }
  };

  // Helper function to get organization name with caching
  const getOrganizationName = async (organizationId) => {
    // Check cache first
    if (organizationCache.has(organizationId)) {
      const org = organizationCache.get(organizationId);
      return org.name || org.organization_name || org.organizationName || organizationId;
    }
    
    try {
      // Fetch organization from API
      const org = await fetchOrganizationById(organizationId);
      
      // Cache the result
      setOrganizationCache(prev => new Map(prev).set(organizationId, org));
      
      return org.name || org.organization_name || org.organizationName || organizationId;
    } catch (error) {
      console.error(`Failed to fetch organization ${organizationId}:`, error);
      return organizationId; // Fallback to ID if fetch fails
    }
  };

  const getSubscriptionTypeName = (subscriptionTypeId) => {
    const type = subscriptionTypes.find(t => t.id === subscriptionTypeId);
    return type ? type.name : subscriptionTypeId;
  };

  // Component for async organization name display
  const OrganizationName = ({ organizationId }) => {
    const [name, setName] = useState(organizationId);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadName = async () => {
        try {
          const orgName = await getOrganizationName(organizationId);
          setName(orgName);
        } catch (error) {
          setName(organizationId);
        } finally {
          setLoading(false);
        }
      };
      loadName();
    }, [organizationId]);

    if (loading) {
      return <Skeleton width={120} height={20} />;
    }

    return <span>{name}</span>;
  };

  const columns = [
    {
      id: 'organizationId',
      label: 'Organization',
      render: (value) => <OrganizationName organizationId={value} />
    },
    {
      id: 'customName',
      label: 'Custom Name',
      render: (value) => value || '-'
    },
    {
      id: 'subscriptionTypeId',
      label: 'Subscription Type',
      render: (value, row) => {
        const label = getSubscriptionTypeName(value) || 'N/A';
        const organizationId = row.organizationId;
        const isDefault = row.isDefault || (organizationId && defaultSubscriptionsMap.get(organizationId) === value);

        if (isDefault) {
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <WalteroTypography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                {label}
              </WalteroTypography>
              <WalteroTypography component="span" variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                DEFAULT
              </WalteroTypography>
            </Box>
          );
        }

        return label;
      }
    },
    {
      id: 'overridePrice',
      label: 'Override Price',
      align: 'right',
      render: (value) => value ? `$${value}` : '-'
    },
    {
      id: 'gracePeriodDays',
      label: 'Grace Period (Days)',
      align: 'center',
      render: (_, row) => {
        const override = row.overrideGracePeriodDays;
        const base = row.gracePeriodDays ?? 0;
        return override === null || override === undefined ? base : override;
      }
    },
    {
      id: 'status',
      label: 'Status',
      align: 'center',
      render: (value) => (
        <Box
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: value === 'active' ? 'success.light' : 'warning.light',
            color: value === 'active' ? 'success.dark' : 'warning.dark',
            textAlign: 'center',
            fontSize: '0.75rem',
            textTransform: 'capitalize'
          }}
        >
          {value}
        </Box>
      )
    },
    {
      id: 'assignedAt',
      label: 'Assigned Date',
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      render: (_, row) => (
        <WalteroButton
          walteroVariant="text"
          size="small"
          disabled={unassigningId === row.id}
          onClick={(event) => handleUnassign(event, row)}
        >
          {unassigningId === row.id ? 'REMOVING...' : 'UNASSIGN'}
        </WalteroButton>
      )
    }
  ];

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        <strong>Error Loading Organization Subscriptions</strong>
        <br />
        {error}
        <Box sx={{ mt: 2 }}>
          <WalteroButton 
            variant="outlined" 
            onClick={loadData}
            size="small"
          >
            RETRY
          </WalteroButton>
        </Box>
      </Alert>
    );
  }

  return (
    <Box>
      <WalteroCard>
        <Box sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Box>
              <WalteroTypography 
                variant="h5" 
                sx={{ 
                  color: 'waltero.primary.coreBlue',
                  fontWeight: 600,
                  mb: 1
                }}
              >
                Organization Subscriptions
              </WalteroTypography>
              <WalteroTypography 
                variant="body2" 
                sx={{ color: 'text.secondary' }}
              >
                View and manage subscription assignments for organizations with custom overrides. Click a row to edit an assignment.
              </WalteroTypography>
            </Box>
            <WalteroButton 
              variant="contained" 
              onClick={() => setAssignModalOpen(true)}
            >
              ASSIGN SUBSCRIPTION
            </WalteroButton>
          </Box>

          {loading ? (
            <Box>
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} height={60} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : organizationSubscriptions.length === 0 ? (
            <Alert severity="info">
              No subscriptions found. Use the button above to assign a subscription to an organization.
            </Alert>
          ) : (
            <WalteroDataTable
              data={organizationSubscriptions}
              columns={columns}
              loading={loading}
              title=""
              showTitle={false}
              filterable={false}
              pagination={false}
              showControls={false}
              onRowClick={(event, row) => {
                if (row?.id) {
                  setSelectedSubscription(row);
                  setEditModalOpen(true);
                }
              }}
            />
          )}
        </Box>
      </WalteroCard>

      <AssignSubscriptionModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        onSuccess={() => {
          loadData(); // Refresh all data
        }}
      />

      <EditOrganizationSubscriptionModal
        open={editModalOpen}
        subscription={selectedSubscription}
        subscriptionTypes={subscriptionTypes}
        defaultSubscriptionsMap={defaultSubscriptionsMap}
        organizationSubscriptionCounts={organizationSubscriptionCounts}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedSubscription(null);
        }}
        onSuccess={(updated) => {
          if (updated?.id && selectedSubscription?.id === updated.id) {
            setSelectedSubscription(prev => ({ ...prev, ...updated, isDefault: updated.isDefault }));
          }
          loadData();
        }}
      />
    </Box>
  );
};

export default OrganizationSubscriptions;
